// SCRUploadPage.jsx — F2 do flow SCR Bacen
// Página acessada pelo TOMADOR via link com JWT: /scr-upload?t=<token>
//
// Sem auth de analista; valida JWT na primeira chamada.
// Fluxo: tutorial Bacen → upload PDF → preview campos → approve/reject.
//
// Para integrar no App.jsx, importar este arquivo e adicionar detecção:
//   const isSCRUpload = window.location.pathname === "/scr-upload";
//   if (isSCRUpload) return <SCRUploadPage />;
//
// Antes do bloco `if (user) return ...`. Sem auth de analista necessária.

import React, { useState, useEffect, useCallback, useRef } from "react";

// API base — pega do mesmo .env do App.jsx via window.__API__ ou fallback
const API = (typeof window !== "undefined" && window.__API__) || "https://api.financialmind.ai";
const PRS_BASE = (typeof window !== "undefined" && window.__PRS_BASE__) || "https://api.financialmind.ai";

// Cores (alinhadas com o resto do app)
const C = {
  bg: "#faf7f2",
  text: "#1a1f2e",
  azure: "#1a3f6f",
  green: "#0f7a3f",
  red: "#dc2626",
  amber: "#f59e0b",
  gray: "#6b7280",
  card: "#fff",
  border: "#e5e7eb",
};

// ── Helpers ───────────────────────────────────────────────────────
function formatBRL(n) {
  if (n == null || isNaN(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

async function fetchJSON(url, opts = {}) {
  const r = await fetch(url, opts);
  const d = await r.json();
  if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
  return d;
}

// ── Tutorial inline (3 passos) ────────────────────────────────────
function BacenTutorial() {
  const [step, setStep] = useState(0);
  const steps = [
    {
      title: "1. Acesse o portal Registrato do Bacen",
      body: (
        <>
          Acesse <a href="https://www.bcb.gov.br/cidadaniafinanceira/registrato"
            target="_blank" rel="noreferrer" style={{ color: C.azure, fontWeight: 600 }}>
            bcb.gov.br/registrato</a> e faça login com sua conta gov.br nível Prata ou Ouro.
          <br/><br/>
          Se ainda não tem cadastro, crie em ~3 minutos com sua identidade digital.
        </>
      ),
    },
    {
      title: "2. Solicite o relatório SCR — Sistema de Informações de Crédito",
      body: (
        <>
          No menu, clique em <b>"Relatórios" → "SCR — Sistema de Informações de Crédito"</b> e
          selecione <b>data-base do mês mais recente</b> disponível.
          <br/><br/>
          Marque a opção <b>"PDF"</b> e baixe. Tamanho típico: 200-500 KB. Validade do relatório: 30 dias.
        </>
      ),
    },
    {
      title: "3. Faça upload aqui no painel",
      body: (
        <>
          Arraste o PDF na área abaixo (ou clique pra escolher).
          O sistema extrai automaticamente: saldo total, valores vencidos, prejuízo, número de instituições.
          <br/><br/>
          <b>Você revisa os dados antes de aprovar.</b> Nada é compartilhado sem sua confirmação.
        </>
      ),
    },
  ];

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: 20, marginBottom: 16,
    }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {steps.map((_, i) => (
          <button key={i} onClick={() => setStep(i)} style={{
            flex: 1, padding: "8px 12px",
            background: step === i ? C.azure : "#f3f4f6",
            color: step === i ? "#fff" : C.gray,
            border: "none", borderRadius: 6, cursor: "pointer",
            fontSize: 12, fontWeight: 600,
          }}>
            Passo {i + 1}
          </button>
        ))}
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{steps[step].title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.6, color: "#374151" }}>{steps[step].body}</div>
    </div>
  );
}

// ── Component principal ───────────────────────────────────────────
export default function SCRUploadPage() {
  const [token, setToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null); // {tenant_id, cnpj, expires_at}
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading | processing | ready | failed | active | rejected
  const [uploadId, setUploadId] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [confirmOwnership, setConfirmOwnership] = useState(false);
  const [confirmAuthorization, setConfirmAuthorization] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // ── Token extract from URL ──
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("t");
    if (!t) { setTokenError("Link inválido — token ausente."); return; }
    setToken(t);
    // Decode JWT payload (sem validar — só pra UI mostrar quem é)
    try {
      const payload = JSON.parse(atob(t.split(".")[1]));
      setTokenInfo({
        tenant_id: payload.tenant_id,
        cnpj: payload.cnpj,
        expires_at: new Date(payload.exp * 1000).toLocaleString("pt-BR"),
        purpose: payload.sub,
      });
    } catch (e) {
      setTokenError("Token mal-formatado.");
    }
  }, []);

  // ── Polling do status pós-upload ──
  useEffect(() => {
    if (uploadState !== "processing" || !uploadId) return;
    const interval = setInterval(async () => {
      try {
        const r = await fetchJSON(`${PRS_BASE}/scr/status/${uploadId}?t=${token}`);
        if (r.state === "ready") {
          setUploadState("ready");
          // Carrega detalhes
          const det = await fetchJSON(`${PRS_BASE}/scr/${uploadId}?t=${token}`);
          setExtracted(det);
          setPdfUrl(det.pdf_url);
          clearInterval(interval);
        } else if (r.state === "failed") {
          setUploadState("failed");
          setError("Não foi possível extrair os dados do PDF. Tente um arquivo mais nítido.");
          clearInterval(interval);
        }
      } catch (e) {
        // tenta de novo na próxima
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [uploadState, uploadId, token]);

  // ── Upload handler ──
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!confirmOwnership || !confirmAuthorization) {
      setError("Confirme as duas declarações antes de enviar.");
      return;
    }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("Apenas PDF é aceito.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Arquivo > 10 MB. Reduza ou comprima.");
      return;
    }

    setError(null);
    setUploadState("uploading");

    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm_ownership", "true");
    fd.append("confirm_authorization", "true");

    try {
      const r = await fetch(`${PRS_BASE}/scr/upload?t=${token}`, {
        method: "POST",
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setUploadId(d.upload_id);
      setUploadState("processing");
    } catch (e) {
      setUploadState("idle");
      setError(`Erro no upload: ${e.message}`);
    }
  }, [confirmOwnership, confirmAuthorization, token]);

  // ── Approve/Reject ──
  const handleApprove = async () => {
    try {
      await fetchJSON(`${PRS_BASE}/scr/${uploadId}/approve?t=${token}`, { method: "POST" });
      setUploadState("active");
    } catch (e) {
      setError(`Erro ao aprovar: ${e.message}`);
    }
  };
  const handleReject = async () => {
    if (!confirm("Tem certeza? A carta será descartada e você precisará subir uma nova.")) return;
    try {
      await fetchJSON(`${PRS_BASE}/scr/${uploadId}/reject?t=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: "reason=rejected_by_uploader",
      });
      setUploadState("rejected");
    } catch (e) {
      setError(`Erro ao rejeitar: ${e.message}`);
    }
  };

  // ── Render ──
  if (tokenError) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 32, maxWidth: 420, textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔒</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: C.red, marginBottom: 8 }}>Acesso negado</div>
          <div style={{ fontSize: 13, color: C.gray }}>{tokenError}</div>
          <div style={{ fontSize: 12, color: C.gray, marginTop: 16 }}>
            Solicite um novo link com seu analista de crédito.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "20px 16px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: C.gray, letterSpacing: 1.5, fontWeight: 600 }}>RISKMIND · FINANCIALMIND.AI</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text, marginTop: 4 }}>Upload da Carta SCR Bacen</div>
          {tokenInfo && (
            <div style={{ fontSize: 12, color: C.gray, marginTop: 4 }}>
              CNPJ: <b>{tokenInfo.cnpj}</b> · Link expira em {tokenInfo.expires_at}
            </div>
          )}
        </div>

        {/* Tutorial */}
        {uploadState === "idle" && <BacenTutorial />}

        {/* Upload Area */}
        {uploadState === "idle" && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Confirmações de autorização</div>
            <label style={{ display: "block", fontSize: 12, marginBottom: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={confirmOwnership} onChange={e => setConfirmOwnership(e.target.checked)} style={{ marginRight: 8 }} />
              Declaro que sou representante legal autorizado da empresa <b>{tokenInfo?.cnpj || "..."}</b>
            </label>
            <label style={{ display: "block", fontSize: 12, marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={confirmAuthorization} onChange={e => setConfirmAuthorization(e.target.checked)} style={{ marginRight: 8 }} />
              Autorizo o RiskMind a extrair os dados do relatório para análise de crédito
            </label>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => {
                e.preventDefault(); setDragOver(false);
                handleFile(e.dataTransfer.files[0]);
              }}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? C.azure : C.border}`,
                borderRadius: 8, padding: 32, textAlign: "center", cursor: "pointer",
                background: dragOver ? "#eff6ff" : "#fafafa",
                transition: "all .2s",
              }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                {dragOver ? "Solte aqui" : "Arraste o PDF aqui ou clique pra escolher"}
              </div>
              <div style={{ fontSize: 11, color: C.gray, marginTop: 4 }}>Máx 10 MB · só PDF</div>
              <input ref={fileInputRef} type="file" accept=".pdf,application/pdf"
                style={{ display: "none" }}
                onChange={e => handleFile(e.target.files[0])} />
            </div>
          </div>
        )}

        {/* Uploading */}
        {uploadState === "uploading" && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 28 }}>⏳</div>
            <div style={{ fontWeight: 600, marginTop: 8 }}>Enviando arquivo...</div>
          </div>
        )}

        {/* Processing */}
        {uploadState === "processing" && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 28 }}>🔍</div>
            <div style={{ fontWeight: 600, marginTop: 8 }}>Extraindo dados do PDF...</div>
            <div style={{ fontSize: 12, color: C.gray, marginTop: 4 }}>Isso leva ~10-30 segundos.</div>
          </div>
        )}

        {/* Ready — preview */}
        {uploadState === "ready" && extracted && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: C.green }}>
              ✅ Dados extraídos — revise antes de aprovar
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Saldo total no SFN" value={formatBRL(extracted.saldo_total)} />
              <Field label="Saldo vencido" value={formatBRL(extracted.saldo_vencidos)} highlight={extracted.saldo_vencidos > 0} />
              <Field label="Prejuízo" value={formatBRL(extracted.saldo_prejuizo)} highlight={extracted.saldo_prejuizo > 0} />
              <Field label="Nº instituições" value={extracted.n_instituicoes || "—"} />
              <Field label="Data de emissão" value={extracted.issued_at || "—"} />
              <Field label="Validade" value={extracted.valid_until || "—"} />
            </div>

            {pdfUrl && (
              <div style={{ marginTop: 16 }}>
                <a href={pdfUrl} target="_blank" rel="noreferrer" style={{ color: C.azure, fontSize: 13 }}>
                  📎 Conferir o PDF original (válido por 5 min)
                </a>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button onClick={handleApprove} style={{
                flex: 1, padding: 12, background: C.green, color: "#fff", border: "none",
                borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600,
              }}>
                Aprovar e enviar
              </button>
              <button onClick={handleReject} style={{
                padding: 12, background: "#fff", color: C.red, border: `1px solid ${C.red}`,
                borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600,
              }}>
                Rejeitar
              </button>
            </div>
          </div>
        )}

        {/* Active (final) */}
        {uploadState === "active" && (
          <div style={{ background: "#dcfce7", border: `1px solid ${C.green}`, borderRadius: 12, padding: 32, textAlign: "center" }}>
            <div style={{ fontSize: 36 }}>🎉</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.green, marginTop: 8 }}>Carta aprovada e ativa!</div>
            <div style={{ fontSize: 13, color: "#166534", marginTop: 8 }}>
              Seu analista de crédito foi notificado. Você pode fechar esta página.
            </div>
          </div>
        )}

        {uploadState === "rejected" && (
          <div style={{ background: "#fee2e2", border: `1px solid ${C.red}`, borderRadius: 12, padding: 24, textAlign: "center" }}>
            <div style={{ fontWeight: 600, color: C.red }}>Carta rejeitada.</div>
            <button onClick={() => { setUploadState("idle"); setUploadId(null); setExtracted(null); }} style={{
              marginTop: 12, padding: "8px 20px", background: C.azure, color: "#fff",
              border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13,
            }}>
              Enviar nova carta
            </button>
          </div>
        )}

        {uploadState === "failed" && (
          <div style={{ background: "#fef3c7", border: `1px solid ${C.amber}`, borderRadius: 12, padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 24 }}>⚠️</div>
            <div style={{ fontWeight: 600, marginTop: 4 }}>Falha na extração</div>
            <div style={{ fontSize: 12, color: C.gray, marginTop: 4 }}>{error}</div>
            <button onClick={() => { setUploadState("idle"); setUploadId(null); setError(null); }} style={{
              marginTop: 12, padding: "8px 20px", background: C.azure, color: "#fff",
              border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13,
            }}>
              Tentar de novo
            </button>
          </div>
        )}

        {/* Error inline (idle state) */}
        {error && uploadState === "idle" && (
          <div style={{ background: "#fee2e2", border: `1px solid ${C.red}`, borderRadius: 8, padding: 12, marginTop: 12, fontSize: 12, color: C.red }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 32, fontSize: 10, color: C.gray }}>
          Keymind Tecnologia em Gestão Ltda · CNPJ 03.984.421/0001-92
          <br/>
          Os dados desta carta são processados conforme LGPD e usados apenas para análise de crédito.
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.gray, fontWeight: 600, letterSpacing: 0.5 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: highlight ? C.red : C.text, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}
